<script setup lang="ts">
import { createSpeedBump } from '@shared/createSpeedBump'
import type {
  LandingPageTextModule as LandingPageTextModuleProps,
} from '@consumer/types/serializers'
const { textModule } = defineProps<{
  // The admin editor renders this same preview from its own serializer, which types
  // desktopHeading as `string | null` because the form writes null to clear it.
  textModule: Omit<LandingPageTextModuleProps, 'desktopHeading'> & { desktopHeading?: string | null }
}>()

onMounted(async () => {
  createSpeedBump('.landing-page-text-module-body')
})
</script>
<template>
  <div
    data-model-type="LandingPageTextModule"
    class="landing-page-text-module font-sans"
  >
    <h3>
      <span
        class="lg:hidden visible"
        v-html="textModule.mobileHeading"
      />
      <span
        class="lg:block hidden"
        v-html="textModule.desktopHeading || textModule.mobileHeading"
      />
    </h3>
    <div class="landing-page-text-module-body" v-html="textModule.body"/>
  </div>
</template>
<style scoped lang="scss">
.landing-page-text-module {
  @apply flex flex-col px-75.5px gap-12px;

  @supports (height: 1lh) {
    :deep(p):empty {
      height: 1lh;
    }
  }

  @supports not (height: 1lh) {
    :deep(p):empty::after {
      content: "\00A0";
    }
  }

  background-color: var(--section-bg-color-name);

  > h3 {
    color:
      var(
        --primary-text-color-name,
        var(--landing-page-text-module-primary-text-color-dark)
      );
    font-size: 1.5rem;
    line-height: 1.1;
  }

  .landing-page-text-module-body {
    @apply font-normal text-base;

    :deep(p),
    :deep(li) {
      @apply lg:text-[1.125rem] text-[1rem];
    }

    color: var(--secondary-text-color-name, var(--landing-page-text-module-secondary-text-color-dark));
    line-height: 140%;

    :deep(a) {
      text-decoration: underline;
    }

    // The global typography reset strips markers, margin and padding from every
    // ul/ol. `revert` drops the whole author origin for these properties, handing
    // the editor-authored body HTML back to the browser defaults.
    :deep(ul),
    :deep(ol) {
      list-style: revert;
      margin: revert;
      padding: revert;
    }
  }
}
</style>
